import { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import Toast from 'react-native-toast-message';
import Input from '@/components/General/inputs/Input';
import InputSection from '@/components/General/inputs/InputSection';
import Toggle from '@/components/General/inputs/Toggle';
import Chip from '@/components/General/Chip';
import Callout from '@/components/General/Callout';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import StepEditorSheet from '@/components/FollowUp/Paths/StepEditorSheet';
import { Mutation } from '@/utils/react-query/Mutations';
import { Query } from '@/utils/react-query/Queries';
import { getPeopleByIds } from '@/data/people';
import { extractFollowUpCode } from '@/data/followUp';
import { showAlert } from '@/utils/showAlert';
import {
    buildPublishPayload,
    definitionToDraft,
    describeStep,
    emptyStep,
    isLocalID,
    stepTitle,
} from '@/utils/followUpPaths';
import { getStepActionIcon } from '@/utils/constants/followUp';

const reorder = (arr, from, delta) => {
    const to = from + delta;
    if (to < 0 || to >= arr.length) return arr;
    const next = [...arr];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
};

const MoveButtons = ({ onUp, onDown, disableUp, disableDown, testIDPrefix }) => (
    <View style={moveStyles.wrap}>
        <Pressable
            testID={`${testIDPrefix}-up`}
            accessibilityRole="button"
            accessibilityLabel="Move up"
            disabled={disableUp}
            onPress={onUp}
            hitSlop={6}
            style={[moveStyles.btn, disableUp && moveStyles.btnDisabled]}
        >
            <Unicon name="chevron-up" size={15} colorVariant={disableUp ? 'textTertiary' : 'textSecondary'} />
        </Pressable>
        <Pressable
            testID={`${testIDPrefix}-down`}
            accessibilityRole="button"
            accessibilityLabel="Move down"
            disabled={disableDown}
            onPress={onDown}
            hitSlop={6}
            style={[moveStyles.btn, disableDown && moveStyles.btnDisabled]}
        >
            <Unicon name="chevron-down" size={15} colorVariant={disableDown ? 'textTertiary' : 'textSecondary'} />
        </Pressable>
    </View>
);

const moveStyles = StyleSheet.create(() => ({
    wrap: { gap: 0 },
    btn: { padding: 3, _web: { cursor: 'pointer' } },
    btnDisabled: { opacity: 0.4 },
}));

/**
 * PathEditor — the flat Path template editor (§25.5). ONE ordered step
 * list: wait a number of days, do or assign the action, continue when it
 * finishes or expires. The whole definition publishes atomically through
 * follow_up_publish_path with expectedRevision — a FOLLOW_UP_CONFLICT
 * means someone else published, so the editor offers a reload rather than
 * clobbering (§25.13).
 *
 * Removing a published step on a template with active enrollments asks
 * first: started work keeps its recorded outcome, unstarted progress
 * retires out of the progress denominator.
 */
const PathEditor = ({ organizationID, path = null, onPublished, onCancel, onReload }) => {
    const router = useRouter();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const canViewAutomations = !!loggedInUser?.can?.('View Paths') || !!loggedInUser?.isAdmin;
    const isCreate = !path?.pathID;
    const [draft, setDraft] = useState(() => definitionToDraft(path));
    const [dirty, setDirty] = useState(isCreate);
    const [errors, setErrors] = useState([]);
    const [editingStep, setEditingStep] = useState(null); // {stepIdx, draftStep}

    // A refetched definition resets the editor only when untouched — local
    // work-in-progress is never clobbered by a background refetch (§25.13).
    useEffect(() => {
        if (path && !dirty) {
            setDraft(definitionToDraft(path));
        }
    }, [path, dirty]);

    // Resolve person/template ids to display names for step summaries.
    const { personIDs, templateIDs } = useMemo(() => {
        const people = new Set();
        const templates = new Set();
        (draft.steps || []).forEach((step) => {
            const a = step?.config?.assignment || {};
            if (a.mode === 'SPECIFIC' && a.personID) people.add(a.personID);
            (a.poolMemberIDs || []).forEach((id) => id && people.add(id));
            if (step?.config?.emailTemplateID) templates.add(step.config.emailTemplateID);
        });
        return { personIDs: [...people], templateIDs: [...templates] };
    }, [draft.steps]);

    const { data: people = [] } = useQuery({
        queryKey: ['people', 'byIds', [...personIDs].sort().join(',')],
        queryFn: () => getPeopleByIds(personIDs, ['id', 'firstName', 'lastName']),
        enabled: personIDs.length > 0,
        staleTime: 1000 * 60 * 5,
    });
    const { data: emailTemplates = [] } = Query.emailTemplates.all({
        options: { enabled: templateIDs.length > 0 },
    });

    const nameMaps = useMemo(() => ({
        peopleMap: Object.fromEntries((people || []).map((p) => [
            p.id, `${p.firstName || ''} ${p.lastName || ''}`.trim(),
        ])),
        templateMap: Object.fromEntries((emailTemplates || []).map((t) => [t.id, t.name])),
    }), [people, emailTemplates]);

    const namesFor = (step) => ({
        templateName: nameMaps.templateMap[step?.config?.emailTemplateID],
        assigneeName: nameMaps.peopleMap[step?.config?.assignment?.personID],
        poolNames: (step?.config?.assignment?.poolMemberIDs || [])
            .map((id) => nameMaps.peopleMap[id])
            .filter(Boolean),
    });

    const publishMutation = Mutation.followUp.publishPath({
        onSuccess: (data) => {
            const summary = data?.summary;
            const bits = [];
            if (summary?.enrollmentsReconciled != null) bits.push(`${summary.enrollmentsReconciled} enrollment${summary.enrollmentsReconciled === 1 ? '' : 's'} updated`);
            if (summary?.stepsRetired) bits.push(`${summary.stepsRetired} step${summary.stepsRetired === 1 ? '' : 's'} retired`);
            Toast.show({
                type: 'success',
                text2: `Path published${bits.length ? ` — ${bits.join(', ')}` : ''}`,
            });
            setDirty(false);
            onPublished?.(data);
        },
        onError: (error) => {
            const code = error?.followUpCode ?? extractFollowUpCode(error);
            if (code === 'FOLLOW_UP_CONFLICT') {
                showAlert({
                    title: 'Definition changed elsewhere',
                    subtitle: 'Someone else published a newer revision of this path. Reload the latest version to keep editing — your current edits will be replaced.',
                    cancelText: 'Keep Editing',
                    confirmText: 'Reload Latest',
                    isDestructive: true,
                    onConfirm: () => {
                        setDirty(false);
                        onReload?.();
                    },
                });
                return;
            }
            if (!error?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to publish the path. Please try again.' });
            }
        },
    });

    const stepOps = useMemo(() => ({
        add: () => {
            setEditingStep({ stepIdx: null, draftStep: emptyStep('OUTREACH') });
        },
        remove: (idx) => {
            setDirty(true);
            setDraft((prev) => ({ ...prev, steps: prev.steps.filter((_, i) => i !== idx) }));
        },
        move: (idx, delta) => {
            setDirty(true);
            setDraft((prev) => ({ ...prev, steps: reorder(prev.steps, idx, delta) }));
        },
        save: (idx, step) => {
            setDirty(true);
            setDraft((prev) => ({
                ...prev,
                steps: idx == null
                    ? [...prev.steps, { ...step, stepID: step.stepID || `local-${Math.random().toString(36).slice(2, 10)}` }]
                    : prev.steps.map((s, i) => (i === idx ? { ...s, ...step, stepID: s.stepID } : s)),
            }));
        },
    }), []);

    const confirmRemoveStep = (step, idx) => {
        const published = !isLocalID(step.stepID);
        // §25.13 — deleting a started step on a Path with live enrollments
        // asks first; finished work keeps its outcome, unstarted progress
        // retires out of the progress denominator.
        if (published && (draft.activeEnrollmentCount || 0) > 0) {
            showAlert({
                title: 'Remove step',
                subtitle: `"${stepTitle(step)}" may already have started for ${draft.activeEnrollmentCount} active enrollment${draft.activeEnrollmentCount === 1 ? '' : 's'}. Completed activity is kept — unstarted runs skip this step.`,
                cancelText: 'Keep',
                confirmText: 'Remove',
                isDestructive: true,
                onConfirm: () => stepOps.remove(idx),
            });
            return;
        }
        stepOps.remove(idx);
    };

    const handlePublish = () => {
        const { errors: validationErrors, payload } = buildPublishPayload(draft);
        setErrors(validationErrors);
        if (!organizationID || !payload || publishMutation.isPending) return;
        publishMutation.mutate({ organizationID, payload });
    };

    return (
        <View style={styles.wrap}>
            <ScrollView
                style={styles.scroll}
                contentContainerStyle={styles.content}
                keyboardShouldPersistTaps="handled"
            >
                <InputSection>
                    <Input
                        title="Path name"
                        placeholder="e.g. Guest welcome, New member follow-up"
                        value={draft.name}
                        maxLength={120}
                        required
                        onUpdate={(v) => { setDirty(true); setDraft((p) => ({ ...p, name: v })); }}
                        testID="path-editor-name"
                    />
                    <Input
                        title="Description"
                        placeholder="What this Path is for"
                        value={draft.description}
                        multiline
                        onUpdate={(v) => { setDirty(true); setDraft((p) => ({ ...p, description: v })); }}
                        testID="path-editor-description"
                    />
                    <View style={styles.toggleRow}>
                        <Text style={styles.toggleLabel}>Active — can be started for people</Text>
                        <Toggle
                            testID="path-editor-active"
                            value={draft.isActive}
                            onValueChange={(v) => { setDirty(true); setDraft((p) => ({ ...p, isActive: v })); }}
                        />
                    </View>
                </InputSection>

                <Text style={styles.sectionTitle}>Steps</Text>
                <Text style={styles.sectionHint}>
                    Steps run in order. Each waits its days, does or assigns its action, then continues when it finishes or expires.
                </Text>

                {draft.steps.length === 0 && (
                    <Callout
                        type="info"
                        message="No steps yet. Add at least one — like an automatic email, a phone call, or a custom task."
                    />
                )}

                {draft.steps.map((step, sIdx) => (
                    <View key={step.stepID} style={styles.stepCard} testID={`path-step-${sIdx}`}>
                        <Pressable
                            accessibilityRole="button"
                            onPress={() => setEditingStep({ stepIdx: sIdx, draftStep: step })}
                            style={styles.stepBody}
                            testID={`step-edit-${sIdx}`}
                        >
                            <View style={styles.orderBadge}>
                                <Text style={styles.orderText}>{sIdx + 1}</Text>
                            </View>
                            <View style={styles.stepIcon}>
                                <Unicon name={getStepActionIcon(step)} size={14} colorVariant="paths" />
                            </View>
                            <View style={styles.stepTextWrap}>
                                <Text style={styles.stepName} numberOfLines={1}>
                                    {stepTitle(step, `Step ${sIdx + 1}`)}
                                </Text>
                                <Text style={styles.stepMeta} numberOfLines={2}>
                                    {describeStep(step, sIdx, namesFor(step))}
                                </Text>
                            </View>
                        </Pressable>
                        <MoveButtons
                            testIDPrefix={`step-move-${sIdx}`}
                            disableUp={sIdx === 0}
                            disableDown={sIdx === draft.steps.length - 1}
                            onUp={() => stepOps.move(sIdx, -1)}
                            onDown={() => stepOps.move(sIdx, 1)}
                        />
                        <Pressable
                            testID={`step-remove-${sIdx}`}
                            accessibilityRole="button"
                            accessibilityLabel="Remove step"
                            hitSlop={6}
                            onPress={() => confirmRemoveStep(step, sIdx)}
                            style={styles.removeBtn}
                        >
                            <Unicon name="close" size={15} colorVariant="textSecondary" />
                        </Pressable>
                    </View>
                ))}

                <View style={styles.addStepRow}>
                    <SolidButton
                        testID="path-add-step"
                        size="sm"
                        variant="outlined"
                        type="secondary"
                        iconName="add"
                        text="Add Step"
                        onPress={stepOps.add}
                    />
                </View>

                {errors.length > 0 && (
                    <View testID="path-editor-errors">
                        <Callout
                            type="error"
                            message={`Fix before publishing:\n• ${errors.join('\n• ')}`}
                        />
                    </View>
                )}

                <View style={styles.footer}>
                    {!isCreate && !!path?.revision && (
                        <Chip icon="pricetag-outline" colorVariant="textSecondary">
                            {`Editing revision ${path.revision}`}
                        </Chip>
                    )}
                    <View style={styles.footerButtons}>
                        {!!onCancel && (
                            <SolidButton
                                testID="path-editor-cancel"
                                variant="outlined"
                                type="textSecondary"
                                text="Cancel"
                                onPress={() => {
                                    if (dirty) {
                                        showAlert({
                                            title: 'Discard changes?',
                                            subtitle: 'Your unpublished edits will be lost.',
                                            cancelText: 'Keep Editing',
                                            confirmText: 'Discard',
                                            isDestructive: true,
                                            onConfirm: onCancel,
                                        });
                                    } else {
                                        onCancel();
                                    }
                                }}
                            />
                        )}
                        <SolidButton
                            testID="path-editor-publish"
                            text={isCreate ? 'Publish Path' : 'Publish Changes'}
                            iconName="cloud-upload-outline"
                            disabled={publishMutation.isPending}
                            onPress={handlePublish}
                        />
                    </View>
                </View>
                <Text style={styles.footerHint}>
                    Changes apply to upcoming steps for people already in this Path. Completed activity is kept.
                </Text>
                {/* §25.5 — creation context: explain automatic enrollment.
                    The link pushes (never replaces) so the unsaved draft
                    stays mounted underneath; 'View Paths' gates the link. */}
                <View style={styles.automationNote} testID="path-editor-automations-note">
                    <Unicon name="flash-outline" size={14} colorVariant="paths" />
                    <Text style={styles.automationText}>
                        People can start a Path manually or be enrolled automatically using{' '}
                        {canViewAutomations ? (
                            <Text
                                testID="path-editor-automations-link"
                                accessibilityRole="link"
                                style={styles.automationLink}
                                onPress={() => router.push('/automations')}
                            >
                                Automations
                            </Text>
                        ) : 'Automations'}
                        .
                    </Text>
                </View>
            </ScrollView>

            <StepEditorSheet
                isOpen={!!editingStep}
                onClose={() => setEditingStep(null)}
                step={editingStep?.draftStep}
                index={editingStep?.stepIdx ?? draft.steps.length}
                onSave={(step) => {
                    if (!editingStep) return;
                    stepOps.save(editingStep.stepIdx, step);
                }}
            />
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    wrap: { flex: 1 },
    scroll: { flex: 1 },
    content: { padding: 12, paddingBottom: 60, gap: 10, maxWidth: 700, width: '100%', alignSelf: 'center' },
    toggleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 8,
    },
    toggleLabel: { fontSize: 14, color: colors.text, flex: 1, marginRight: 8 },
    sectionTitle: { fontSize: 13, fontFamily: fonts.heading, color: colors.textSecondary, marginTop: 6 },
    sectionHint: { fontSize: 12, color: colors.textSecondary, lineHeight: 17 },
    stepCard: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 12,
        boxShadow: shadows.card,
    },
    stepBody: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10, minWidth: 0, _web: { cursor: 'pointer' } },
    orderBadge: {
        width: 26,
        height: 26,
        borderRadius: 13,
        backgroundColor: colors.pathsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    orderText: { fontSize: 13, fontFamily: fonts.bold, color: colors.paths },
    stepIcon: {
        width: 28,
        height: 28,
        borderRadius: 7,
        backgroundColor: colors.pathsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    stepTextWrap: { flex: 1, minWidth: 0 },
    stepName: { fontSize: 14, fontFamily: fonts.bold, color: colors.text },
    stepMeta: { fontSize: 12, color: colors.textSecondary, marginTop: 1, lineHeight: 16 },
    removeBtn: { padding: 4, _web: { cursor: 'pointer' } },
    addStepRow: { flexDirection: 'row' },
    footer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, flexWrap: 'wrap', gap: 8 },
    footerButtons: { flexDirection: 'row', gap: 10 },
    footerHint: { fontSize: 11, color: colors.textTertiary, lineHeight: 15 },
    automationNote: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: colors.pathsContainer,
        borderRadius: 10,
        paddingHorizontal: 12,
        paddingVertical: 10,
        marginTop: 6,
    },
    automationText: { flex: 1, fontSize: 12, color: colors.text, lineHeight: 17 },
    automationLink: { fontFamily: fonts.bold, color: colors.paths, _web: { cursor: 'pointer' } },
}));

export default PathEditor;
